import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TrendingUp, AlertTriangle, Eye, Users, FileText, Radio, Newspaper, Eye as EyeIcon, Loader2 } from 'lucide-react'
import { authApi, systemApi, monitorApi, analysisApi, reportApi } from '@/lib/api'
import { useAllProjects } from '@/lib/hooks/useProjects'

export function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projects } = useAllProjects()
  const [onlineUsers, setOnlineUsers] = useState(0)
  const [warningCount, setWarningCount] = useState(0)
  const [todayArticles, setTodayArticles] = useState(0)
  const [hotEvents, setHotEvents] = useState(0)
  const [loading, setLoading] = useState(true)
  const [hotTopics, setHotTopics] = useState<any[]>([])
  const [recentReports, setRecentReports] = useState<any[]>([])

  // Load online stats, warning count, and recent reports
  useEffect(() => {
    authApi.getOnlineStatistics().then((data: any) => {
      setOnlineUsers(data?.data?.online_count || data?.online_count || 0)
    }).catch(() => {})

    systemApi.getWarningArticle(1).then((data: any) => {
      const d = data?.data || data
      setWarningCount(d?.total || (Array.isArray(d) ? d.length : 0))
    }).catch(() => {})

    reportApi.list({ pageNum: 1 }).then((data: any) => {
      const d = data?.data || data || []
      setRecentReports(Array.isArray(d) ? d.slice(0, 4) : [])
    }).catch(() => {})
  }, [])

  // Load article count and hot events when projects are available
  useEffect(() => {
    if (projects.length === 0) return
    const pid = projects[0].project_id
    Promise.allSettled([
      analysisApi.dataOverview(pid, 2),
      analysisApi.hotEventRanking(pid, 2),
    ]).then(([overview, events]) => {
      if (overview.status === 'fulfilled') {
        const ov = (overview.value as any)?.data || overview.value
        setTodayArticles(ov?.total || 0)
      }
      if (events.status === 'fulfilled') {
        const ev = (events.value as any)?.data || events.value || []
        setHotEvents(Array.isArray(ev) ? ev.length : 0)
        // Use hot events as hot topics
        const topics = (Array.isArray(ev) ? ev : []).slice(0, 5).map((e: any, i: number) => ({
          rank: i + 1,
          title: e.event_name || e.name || '',
          heat: `${e.count || 0}条`,
        }))
        setHotTopics(topics)
      }
    }).finally(() => setLoading(false))
  }, [projects])

  const stats = [
    { 
      label: t('dashboard.todayStatus'), 
      value: loading ? '...' : todayArticles.toString(), 
      icon: TrendingUp, 
      color: 'text-blue-500',
      bg: 'bg-blue-50 dark:bg-blue-950/30',
    },
    { 
      label: t('dashboard.warningAlerts'), 
      value: warningCount.toString(), 
      icon: AlertTriangle, 
      color: 'text-red-500',
      bg: 'bg-red-50 dark:bg-red-950/30',
      onClick: () => navigate({ to: '/settings/warning-msg' }),
    },
    { 
      label: t('dashboard.hotEvents'), 
      value: hotEvents.toString(), 
      icon: Eye, 
      color: 'text-green-500',
      bg: 'bg-green-50 dark:bg-green-950/30',
      onClick: () => navigate({ to: '/publicoption' }),
    },
    { 
      label: t('dashboard.onlineUsers'), 
      value: onlineUsers.toString(), 
      icon: Users, 
      color: 'text-purple-500',
      bg: 'bg-purple-50 dark:bg-purple-950/30',
    },
  ]

  const hotPlatforms = [
    { key: 'hotWeibo', icon: '🔥', label: t('dashboard.hotWeibo') },
    { key: 'hotWechat', icon: '💬', label: t('dashboard.hotWechat') },
    { key: 'hotDouyin', icon: '🎵', label: t('dashboard.hotDouyin') },
    { key: 'hotBilibili', icon: '📺', label: t('dashboard.hotBilibili') },
  ]

  const quickActions = [
    { label: t('nav.monitorManage'), icon: EyeIcon, path: '/monitor' },
    { label: t('nav.monitorData'), icon: Newspaper, path: '/monitor/data' },
    { label: t('nav.publicOption'), icon: Radio, path: '/publicoption' },
    { label: t('nav.report'), icon: FileText, path: '/report' },
  ]

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <Card 
              key={stat.label} 
              className={`cursor-pointer transition-all hover:shadow-md ${stat.onClick ? 'hover:-translate-y-0.5' : ''}`}
              onClick={stat.onClick}
            >
              <CardContent className="flex items-center justify-between p-6">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${stat.bg}`}>
                  <Icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {quickActions.map((action) => {
          const Icon = action.icon
          return (
            <Button
              key={action.path}
              variant="outline"
              className="h-20 flex-col gap-2"
              onClick={() => navigate({ to: action.path })}
            >
              <Icon className="h-5 w-5" />
              <span className="text-xs">{action.label}</span>
            </Button>
          )
        })}
      </div>

      {/* Hot topics grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Hot topics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              🔥 热点事件
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {hotTopics.map((topic) => (
                <div key={topic.rank} className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${topic.rank <= 3 ? 'bg-orange-100 text-orange-600' : 'bg-muted text-muted-foreground'}`}>
                      {topic.rank}
                    </span>
                    <span className="text-sm">{topic.title}</span>
                  </div>
                  <Badge variant="secondary">{topic.heat}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent reports */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              📊 {t('dashboard.dailyReport')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentReports.length > 0 ? recentReports.map((report) => (
                <div key={report.report_id} className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50 transition-colors cursor-pointer" onClick={() => navigate({ to: `/report/${report.report_id}` })}>
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{report.report_name}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs">
                    {t('report.viewDetail')}
                  </Button>
                </div>
              )) : [1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{t('common.noData')}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Platform hot topics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {hotPlatforms.map((platform) => (
          <Card key={platform.key}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                {platform.icon} {platform.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {hotTopics.slice(0, 3).map((topic) => (
                  <div key={topic.rank} className="truncate text-xs py-1 hover:text-primary cursor-pointer">
                    {topic.title}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
